import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import moment from "moment";
import { db } from "../lib/firebase";
import "../styles/profile.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserLock } from "@fortawesome/free-solid-svg-icons";
import Loader from "../components/Loader";
import Verification from "../components/dashboard/Verification";
import NotFound from "../pages/NotFound";

const Profile = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [updatedUser, setUpdatedUser] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [balance, setBalance] = useState(0);
  const [showVerificationBox, setShowVerificationBox] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser(userData);
          setUpdatedUser(userData);

          const userTransactions = userData.transactions || [];
          const userBalance = userTransactions.reduce((total, transaction) => {
            if (transaction.account === "Trading") return total;
            return transaction.type === "credit"
              ? total + transaction.amount
              : total - transaction.amount;
          }, 0);
          setBalance(userBalance);
        } else {
          console.log("No such document!");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user: ", error);
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser({ ...updatedUser, [name]: value });
  };

  const handleSave = async () => {
    try {
      await updateDoc(doc(db, "users", userId), updatedUser);
      setUser(updatedUser);
      setEditMode(false);
      setSuccessMessage("Update successful!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error updating user: ", error);
    }
  };

  const handleForgotPassword = async () => {
    const auth = getAuth();
    if (!user?.email) {
      setUploadError("User email not found.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, user.email);
      setSuccessMessage(`Password reset email sent to ${user.email}`);
      setTimeout(() => setSuccessMessage(""), 8000);
    } catch (error) {
      setUploadError(`Error sending password reset email: ${error.message}`);
      console.error("Error sending reset email:", error.message);
    }
  };

  if (loading) return <Loader />;
  if (!user) return <NotFound />;

  const userGroup = user?.group || "inactive";

  return (
    <div className="profile-page">
      <div className="profile-card">
        <div className="profile-grid">
          <div className="profile-field">
            <div
              className="profile-header-item"
              style={{ marginBottom: "15px" }}
            >
              <div>
                <b className="profile-label">User Information</b>
                <div
                  className="profile-value"
                  style={{ fontSize: "1.4rem", marginBottom: "4px" }}
                >
                  {user.fullName}
                </div>
                <div
                  className="profile-value"
                  style={{ opacity: 0.7, fontSize: "0.9rem" }}
                >
                  {user.email}
                </div>
              </div>
              <i
                onClick={handleForgotPassword}
                title="Reset Password"
                className="profile-reset-icon fas fa-repeat"
              ></i>
            </div>
            <div
              style={{
                marginTop: "10px",
                paddingTop: "15px",
                borderTop: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <b className="profile-label">Available Balance</b>
              <div
                className="profile-value"
                style={{ fontSize: "1.6rem", fontWeight: "bold" }}
              >
                $
                {balance.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
            {uploadError && <p className="profile-error">{uploadError}</p>}
          </div>

          <div className="profile-field">
            <b className="profile-label">Transaction Report</b>
            <div className="profile-value">
              {user.report || "No Report Available"}
            </div>
            {userGroup === "deactivated" && (
              <button
                className="profile-verify-btn"
                onClick={() => setShowVerificationBox(true)}
              >
                Verify Transactions
                <FontAwesomeIcon icon={faUserLock} />
              </button>
            )}
          </div>
        </div>

        <div>
          <h3 className="profile-section-title">User Details</h3>
          <div className="profile-grid">
            <div className="profile-field">
              <b className="profile-label">Full Name</b>
              {editMode ? (
                <input
                  type="text"
                  className="profile-input"
                  name="fullName"
                  value={updatedUser.fullName}
                  onChange={handleChange}
                />
              ) : (
                <div className="profile-value">{user.fullName}</div>
              )}
            </div>
            <div className="profile-field">
              <b className="profile-label">Account Number</b>
              <div className="profile-value">{user.accountNumber}</div>
            </div>
          </div>

          <div className="profile-grid">
            <div className="profile-field">
              <b className="profile-label">Address</b>
              {editMode ? (
                <input
                  type="text"
                  name="address"
                  className="profile-input"
                  value={updatedUser.address}
                  onChange={handleChange}
                />
              ) : (
                <div className="profile-value">{user.address}</div>
              )}
            </div>
            <div className="profile-field">
              <b className="profile-label">Gender</b>
              {editMode ? (
                <select
                  name="gender"
                  className="profile-input"
                  value={updatedUser.gender}
                  onChange={handleChange}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              ) : (
                <div
                  className="profile-value"
                  style={{ textTransform: "capitalize" }}
                >
                  {user.gender}
                </div>
              )}
            </div>
          </div>

          <div className="profile-grid">
            <div className="profile-field">
              <b className="profile-label">Date of Birth</b>
              {editMode ? (
                <input
                  type="date"
                  className="profile-input"
                  name="dob"
                  value={moment(updatedUser.dob).format("YYYY-MM-DD")}
                  onChange={handleChange}
                />
              ) : (
                <div className="profile-value">
                  {moment(user.dob).format("MMMM D, YYYY")}
                </div>
              )}
            </div>
            <div className="profile-field">
              <b className="profile-label">Phone Number</b>
              {editMode ? (
                <input
                  type="text"
                  className="profile-input"
                  name="phoneNumber"
                  value={updatedUser.phoneNumber || ""}
                  onChange={handleChange}
                />
              ) : (
                <div className="profile-value">
                  {user.phoneNumber || "Not Set"}
                </div>
              )}
            </div>
          </div>

          <div className="profile-btn-group">
            {editMode ? (
              <button onClick={handleSave} className="profile-btn">
                Save Changes
              </button>
            ) : (
              <button onClick={() => setEditMode(true)} className="profile-btn">
                Edit Profile
              </button>
            )}
          </div>

          {showVerificationBox && (
            <Verification setShowVerificationBox={setShowVerificationBox} />
          )}

          {successMessage && (
            <div className="profile-success">{successMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
